import { apiFetch, parseApiError } from './client';
import {
  DATA_FRESHNESS_MOCK,
  ERP_EXPORT_SETTINGS_MOCK,
  REVENUE_MOVEMENT_MOCK,
  SCAN_HEATMAP_MOCK,
} from '../data/phase2AnalyticsMock';

function buildQuery(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  return params.toString();
}

async function tryLive(path, fallbackMessage) {
  const response = await apiFetch(path, { method: 'GET' });
  if (response.status === 404) {
    const err = new Error('API not implemented yet');
    err.code = 'NOT_IMPLEMENTED';
    throw err;
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response, fallbackMessage));
  }
  return response.json();
}

function clone(data) {
  return JSON.parse(JSON.stringify(data));
}

/** GET /api/revenue/movement */
export async function fetchRevenueMovement(filters = {}) {
  const query = buildQuery({
    start: filters.start,
    end: filters.end,
    eventId: filters.eventId,
    venueId: filters.venueId,
    accountId: filters.accountId,
  });
  const path = query ? `/api/revenue/movement?${query}` : '/api/revenue/movement';
  try {
    const data = await tryLive(path, 'Failed to load revenue movement');
    return { data, source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED' && filters.allowMock === false) throw error;
    return { data: clone(REVENUE_MOVEMENT_MOCK), source: 'mock' };
  }
}

/** GET /api/scans/heatmap */
export async function fetchScanHeatmap(filters = {}) {
  const query = buildQuery({
    start: filters.start,
    end: filters.end,
    eventId: filters.eventId,
    venueId: filters.venueId,
    accountId: filters.accountId,
    // Board is hour×gate; request 60-min buckets (API default is 30).
    bucketMinutes: filters.bucketMinutes ?? 60,
  });
  const path = query ? `/api/scans/heatmap?${query}` : '/api/scans/heatmap';
  try {
    const data = await tryLive(path, 'Failed to load scan heatmap');
    return { data, source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED' && filters.allowMock === false) throw error;
    return { data: clone(SCAN_HEATMAP_MOCK), source: 'mock' };
  }
}

/** GET /api/data-freshness */
export async function fetchDataFreshness({ accountId } = {}) {
  const query = buildQuery({ accountId });
  const path = query ? `/api/data-freshness?${query}` : '/api/data-freshness';
  try {
    const data = await tryLive(path, 'Failed to load data freshness');
    return { data, source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED') {
      // Soft-fail: banner should not break the dashboard.
      return { data: clone(DATA_FRESHNESS_MOCK), source: 'mock', error: error.message };
    }
    return { data: clone(DATA_FRESHNESS_MOCK), source: 'mock' };
  }
}

/** GET /api/erp/export-settings */
export async function fetchErpExportSettings({ accountId, provider = 'dynamics' } = {}) {
  const query = buildQuery({ accountId, provider });
  const path = query ? `/api/erp/export-settings?${query}` : '/api/erp/export-settings';
  try {
    const data = await tryLive(path, 'Failed to load ERP export settings');
    return { data, source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED' && accountId === undefined) throw error;
    const data = clone(ERP_EXPORT_SETTINGS_MOCK);
    data.provider = provider;
    data.accountId = accountId ?? null;
    return { data, source: 'mock' };
  }
}

/** PATCH /api/erp/export-settings */
export async function saveErpExportSettings(payload, { accountId } = {}) {
  const query = buildQuery({ accountId });
  const path = query ? `/api/erp/export-settings?${query}` : '/api/erp/export-settings';
  try {
    const response = await apiFetch(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (response.status === 404) {
      return {
        data: { ...clone(ERP_EXPORT_SETTINGS_MOCK), ...payload, accountId: accountId ?? null },
        source: 'mock',
      };
    }
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to save ERP export settings'));
    }
    return { data: await response.json(), source: 'api' };
  } catch (error) {
    if (error.message?.includes('Failed to save')) throw error;
    return {
      data: { ...clone(ERP_EXPORT_SETTINGS_MOCK), ...payload, accountId: accountId ?? null },
      source: 'mock',
    };
  }
}
